import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, Loader2 } from "lucide-react";

export function Stars({ value, onChange, size = "w-7 h-7" }) {
  return (
    <div className="flex gap-1" data-testid="rating-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" disabled={!onChange} onClick={() => onChange?.(n)} data-testid={`rating-star-${n}`} aria-label={`${n} star`}>
          <Star className={`${size} transition-colors ${n <= value ? "fill-[#F5C32C] text-[#F5C32C]" : "text-slate-300"}`} />
        </button>
      ))}
    </div>
  );
}

export function RateUsCard() {
  const [state, setState] = useState(null);
  const [rating, setRating] = useState(0);
  const [improve, setImprove] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { api.get("/feedback/mine").then(({ data }) => setState(data)).catch(() => {}); }, []);
  if (!state?.eligible || state.feedback || done === "hidden") return null;

  const submit = async () => {
    if (!rating) { toast.error("Tap a star rating first."); return; }
    setBusy(true);
    try { await api.post("/feedback", { rating, improve }); setDone(true); toast.success("Thank you for your feedback!"); }
    catch (e) { toast.error("Could not send feedback. Try again."); }
    finally { setBusy(false); }
  };

  if (done) return <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-800" data-testid="rate-thanks">Thank you! Your feedback helps us improve Your Varsity Application.</div>;
  return (
    <div className="mt-6 rounded-xl border border-[#F5C32C]/60 bg-[#FFF9E6] p-5" data-testid="rate-us-card">
      <p className="font-display font-bold text-[#0B2447]">Your application is done — how did we do?</p>
      <p className="text-sm text-slate-600 mt-1">Rate our system and tell us what we should improve.</p>
      <div className="mt-3"><Stars value={rating} onChange={setRating} /></div>
      <Textarea value={improve} onChange={(e) => setImprove(e.target.value)} maxLength={1500} rows={3} className="mt-3 bg-white" placeholder="What should we improve?" data-testid="rate-improve" />
      <div className="mt-3 flex gap-2">
        <Button onClick={submit} disabled={busy} className="bg-[#0B2447] hover:bg-[#153461]" data-testid="rate-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send feedback"}</Button>
        <Button variant="ghost" onClick={() => setDone("hidden")} data-testid="rate-later">Maybe later</Button>
      </div>
    </div>
  );
}

export function FeedbackTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/feedback").then(({ data: d }) => setData(d)).catch(() => {}); }, []);
  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-[#0B2447]" />;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="feedback-tab">
      <div className="flex flex-wrap items-end gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] text-slate-500">Average rating</p>
          <p className="font-display text-3xl font-bold text-[#0B2447]" data-testid="feedback-average">{data.average || "—"}<span className="text-base text-slate-400"> / 5</span></p>
          <p className="text-xs text-slate-500">{data.count} review{data.count === 1 ? "" : "s"}</p>
        </div>
        <div className="flex-1 min-w-[200px] space-y-1">
          {[5, 4, 3, 2, 1].map((n) => {
            const c = data.distribution[n] || 0;
            return (
              <div key={n} className="flex items-center gap-2 text-xs">
                <span className="w-4">{n}★</span>
                <div className="flex-1 h-2 rounded bg-slate-100"><div className="h-2 rounded bg-[#F5C32C]" style={{ width: `${data.count ? (c / data.count) * 100 : 0}%` }} /></div>
                <span className="w-6 text-right text-slate-500">{c}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-6 divide-y divide-slate-100">
        {data.items.length === 0 && <p className="text-sm text-slate-400">No feedback yet. Students are asked after their application is done.</p>}
        {data.items.map((f) => (
          <div key={f.id} className="py-3" data-testid={`feedback-${f.id}`}>
            <div className="flex flex-wrap items-center gap-3"><Stars value={f.rating} size="w-4 h-4" />
              <span className="text-sm font-medium text-[#0B2447]">{f.name || f.email}</span>
              <span className="text-xs text-slate-400">{f.email} · {new Date(f.updated_at).toLocaleDateString()}</span></div>
            {f.improve ? <p className="text-sm text-slate-700 mt-1.5 whitespace-pre-wrap">{f.improve}</p> : <p className="text-xs text-slate-400 mt-1">No comment.</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
